import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DeletModel from "../../model/DeletModel";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { GET_USER_LIST } from "../../../api/url";
import Loader from "../../../components/common/Loader";
import { arrowBack, arrowForward } from "../../../assets/image";
import { strings } from "../../../strings/strings";
import { toast } from "react-toastify";

const UserList = () => {
  const navigate = useNavigate();
  const { mutate: deleteUser } = useAppMutation();

  const [page, setPage] = useState(1);

  const {
    data: userData,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useAppQuery({
    queryKey: [QUERY_KEYS.USER_LIST, page],
    url: `${GET_USER_LIST}?page=${page}`,
  });

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleDeleteClick = (user) => {
    setSelectedUserId(user?._id);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      deleteUser(
        {
          url: `${GET_USER_LIST}?userId=${selectedUserId}`,
          method: "DELETE",
        },
        {
          onSuccess: () => {
            setShowModal(false);
            toast.success(strings.userDeleteSuccess);
            setTimeout(() => {
              setPage(1);
              refetch();
              setSelectedUserId(null);
            }, 2000);
          },
        }
      );
    } catch (err) {}
  };

  const handleUserProfile = (user) => {
    navigate("/admin/user/userDetails", { state: { userId: user._id } });
  };

  const handleCancelDelete = () => {
    setShowModal(false);
    setSelectedUserId(null);
  };

  if (isLoading || isRefetching) {
    return <Loader />;
  }

  if (isError) {
    return <div className="p-4 text-red-600">Failed to load users.</div>;
  }

  const { pagination, data } = userData?.results ?? {};
  const { currentPage, totalPages } = pagination ?? {};

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-primary">Users</h1>
          <p className="mt-2 text-sm text-gray-700 capitalize">
            A list of all users including their name  , dob , gender and mobile no.
          </p>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-dark sm:pl-6">
                      User Name
                    </th>
         
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-dark">
                      Dob
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-dark">
                      Gender
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-dark">
                      Mobile No
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-dark">
                      State
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-dark">
                      Country
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-dark">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data?.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-dark sm:pl-6">
                        {user?.userName ? user?.userName : "-"}
                      </td>
                
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-grey">{user.dob ? user?.dob : "-"}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-grey">
                        {user.gender ? user?.gender : "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-grey">
                        {user?.mobileNumber ? user?.mobileNumber : "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-grey">
                        {user?.state ? user.state : "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-grey">
                        {user?.country ? user.country : "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-grey">
                        <div className="flex gap-3 justify-center">
                          <button
                            onClick={() => handleUserProfile(user)}
                            className="text-primary text-sm sm:text-base font-semibold"
                          >
                            View
                          </button>
                          |
                          <button
                            onClick={() => handleDeleteClick(user)}
                            className="text-red-700 text-sm sm:text-base font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center space-x-2">
          <button
            onClick={() => setPage((old) => Math.max(old - 1, 1))}
            disabled={page === 1}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-md border text-sm font-medium ${
              page === 1
                ? "bg-gray-200 text-light cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
            }`}
          >
            <img src={arrowBack} alt="" />
          </button>

          {Array.from({ length: totalPages }, (_, index) =>
            index < 2 || index > totalPages - 3 || Math.abs(page - (index + 1)) <= 1 ? (
              <button
                key={index}
                onClick={() => setPage(index + 1)}
                className={`px-3 sm:px-4 py-2 rounded-md border text-sm font-medium ${
                  currentPage === index + 1
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
                }`}
              >
                {index + 1}
              </button>
            ) : null
          )}

          <button
            onClick={() => setPage((old) => old + 1)}
            disabled={page === totalPages}
            className={`flex items-center px-2 sm:px-3 py-2 rounded-md border text-sm font-medium ${
              page === totalPages
                ? "bg-gray-200 text-light cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-100 border-gray-300"
            }`}
          >
            <img src={arrowForward} alt="" />
          </button>
        </div>
      )}
      {showModal && <DeletModel handleConfirmDelete={handleConfirmDelete} handleCancelDelete={handleCancelDelete} />}
    </div>
  );
};

export default UserList;
